import { useCallback, useEffect, useRef, useState } from "react";
import assetClient from "src/services/assetClient";

/**
 * Real-time chunked video upload to /api/v1.0/upload/video/chunked.
 *
 * Backend slices and reassembles via Redis-tracked bitmap; the
 * frontend's job here is to:
 *   1. POST initiate
 *   2. Slice the File client-side into matching chunk sizes and
 *      upload them in parallel (default 3 in flight)
 *   3. POST complete to assemble + hand off to Bitmovin
 *
 * Per-chunk progress is summed across in-flight uploads so the bar
 * moves smoothly even when only one chunk is active. Speed and ETA
 * are derived from a 5-second rolling window of bytes uploaded.
 *
 * Errors on a chunk trigger a bounded retry (3 attempts, exponential
 * backoff). Persistent failure surfaces via `error`. Aborting cancels
 * in-flight chunk requests AND tells the backend to discard state.
 */

const PARALLELISM = 3;
const MAX_CHUNK_RETRIES = 3;
const BACKOFF_BASE_MS = 800;
const SPEED_WINDOW_MS = 5_000;
// Throttle the progress setState so very fast connections don't flood
// React with re-renders. Internal refs always update synchronously so
// correctness is preserved; this only gates the visible state push.
const PROGRESS_EMIT_INTERVAL_MS = 100;

export type ChunkedUploadStatus =
	| "idle"
	| "initiating"
	| "uploading"
	| "completing"
	| "done"
	| "error"
	| "aborted";

export interface ChunkedUploadState {
	status: ChunkedUploadStatus;
	bytesUploaded: number;
	totalBytes: number;
	progress: number; // 0..1
	speedBytesPerSecond: number; // smoothed over a 5s window
	etaSeconds: number | null;
	assetId: string | null;
	error: string | null;
}

const initialState = (): ChunkedUploadState => ({
	status: "idle",
	bytesUploaded: 0,
	totalBytes: 0,
	progress: 0,
	speedBytesPerSecond: 0,
	etaSeconds: null,
	assetId: null,
	error: null
});

interface InitiateResponse {
	upload_id: string;
	chunk_size_bytes: number;
	total_chunks: number;
}

interface CompleteResponse {
	asset_id: string;
	success: boolean;
}

const sleep = (ms: number) =>
	new Promise<void>((resolve) => setTimeout(resolve, ms));

export function useChunkedVideoUpload() {
	const [state, setState] = useState<ChunkedUploadState>(initialState);
	const abortRef = useRef<AbortController | null>(null);
	const uploadIdRef = useRef<string | null>(null);
	// Set by `uploadChunkWithRetry` when a chunk's retry budget is
	// exhausted. The outer `upload()` catch consults this to decide
	// whether the failure should be reported as `error` (chunk failure)
	// or `aborted` (user-initiated cancel). Without it, the abort we
	// fire to stop sibling workers would mis-flag the status.
	const fatalChunkFailureRef = useRef<boolean>(false);

	// Per-chunk in-progress byte counters. Sum + completedBytes = total
	// uploaded right now. This lets the UI show smooth progress while
	// individual chunks are mid-flight.
	const inflightBytesRef = useRef<Map<number, number>>(new Map());
	const completedBytesRef = useRef<number>(0);
	const totalBytesRef = useRef<number>(0);

	// Rolling window of (timestamp, bytesUploaded) samples for speed.
	const speedSamplesRef = useRef<{ t: number; bytes: number }[]>([]);

	// Last visible-state emit timestamp; throttles setState to ~10 Hz.
	const lastEmitRef = useRef<number>(0);

	const recomputeProgress = useCallback((force = false) => {
		const now = Date.now();

		// Throttle BOTH the speed-sample append and the visible state
		// emission. axios fires onUploadProgress on every byte on fast
		// connections, which would grow the samples array to thousands of
		// entries within the 5s window — pure CPU/memory waste, since the
		// state UI updates at ~10 Hz anyway. Force-emit on chunk-complete
		// and terminal transitions so the final view is correct.
		if (!force && now - lastEmitRef.current < PROGRESS_EMIT_INTERVAL_MS) {
			return;
		}
		lastEmitRef.current = now;

		const inflight = Array.from(inflightBytesRef.current.values()).reduce(
			(a, b) => a + b,
			0
		);
		const total = totalBytesRef.current;
		const uploaded = Math.min(completedBytesRef.current + inflight, total);

		speedSamplesRef.current.push({ t: now, bytes: uploaded });
		// Trim samples outside the rolling window.
		while (
			speedSamplesRef.current.length > 1 &&
			now - speedSamplesRef.current[0].t > SPEED_WINDOW_MS
		) {
			speedSamplesRef.current.shift();
		}

		const oldest = speedSamplesRef.current[0];
		const dtSeconds = Math.max((now - oldest.t) / 1000, 0.001);
		const dBytes = uploaded - oldest.bytes;
		const speed = dtSeconds > 0 ? Math.max(dBytes / dtSeconds, 0) : 0;
		const remaining = Math.max(total - uploaded, 0);
		const eta = speed > 0 ? Math.round(remaining / speed) : null;

		setState((prev) => ({
			...prev,
			bytesUploaded: uploaded,
			totalBytes: total,
			progress: total > 0 ? uploaded / total : 0,
			speedBytesPerSecond: speed,
			etaSeconds: eta
		}));
	}, []);

	const reset = useCallback(() => {
		setState(initialState());
		inflightBytesRef.current = new Map();
		completedBytesRef.current = 0;
		totalBytesRef.current = 0;
		speedSamplesRef.current = [];
		uploadIdRef.current = null;
		abortRef.current = null;
		fatalChunkFailureRef.current = false;
		lastEmitRef.current = 0;
	}, []);

	const abort = useCallback(async () => {
		// Atomically claim ownership of the controller and uploadId. If
		// another `abort()` raced us (e.g. user double-clicked Cancel,
		// or unmount cleanup ran while a manual abort was in flight),
		// `controller` will be null and we exit cleanly without firing
		// duplicate state transitions or duplicate DELETE requests.
		const controller = abortRef.current;
		const uploadId = uploadIdRef.current;
		abortRef.current = null;
		uploadIdRef.current = null;
		if (!controller && !uploadId) return;
		controller?.abort();
		setState((prev) => {
			// Don't clobber a terminal status that already settled —
			// abort racing with done/error should be a no-op visually.
			if (prev.status === "done" || prev.status === "error") return prev;
			return { ...prev, status: "aborted" };
		});
		if (uploadId) {
			try {
				await assetClient.delete(`/api/v1.0/upload/video/chunked/${uploadId}`);
			} catch {
				// Best-effort: server-side cleanup will reap orphans hourly.
			}
		}
	}, []);

	const uploadChunkWithRetry = useCallback(
		async (
			uploadId: string,
			index: number,
			chunk: Blob,
			controller: AbortController
		) => {
			const { signal } = controller;
			let attempt = 0;
			let lastError: unknown = null;
			while (attempt < MAX_CHUNK_RETRIES) {
				if (signal.aborted) throw new Error("aborted");
				try {
					inflightBytesRef.current.set(index, 0);
					await assetClient.post(
						`/api/v1.0/upload/video/chunked/${uploadId}/chunk/${index}`,
						chunk,
						{
							headers: { "Content-Type": "application/octet-stream" },
							signal,
							onUploadProgress: (e) => {
								// Axios may not always provide total; fall back to
								// the chunk's known size so progress stays accurate.
								// Clamp to chunk.size so a misreported `loaded` can't
								// over-count the visible progress.
								const loaded = e.loaded ?? 0;
								inflightBytesRef.current.set(
									index,
									Math.min(loaded, chunk.size)
								);
								recomputeProgress();
							}
						}
					);
					inflightBytesRef.current.delete(index);
					completedBytesRef.current += chunk.size;
					// Force-emit on chunk completion so the bar always lands
					// on the correct cumulative position.
					recomputeProgress(true);
					return;
				} catch (err) {
					lastError = err;
					inflightBytesRef.current.set(index, 0);
					recomputeProgress();
					attempt++;
					if (signal.aborted) throw err;
					if (attempt < MAX_CHUNK_RETRIES) {
						await sleep(BACKOFF_BASE_MS * 2 ** (attempt - 1));
					}
				}
			}
			// All retries exhausted — flag fatal failure and abort the
			// controller so any sibling workers stop posting chunks
			// immediately. They'd otherwise keep saturating uplink for
			// tens of seconds against an upload the catch is about to
			// mark failed. The `fatalChunkFailureRef` lets the outer
			// catch distinguish this case from a user-initiated cancel.
			fatalChunkFailureRef.current = true;
			controller.abort();
			throw lastError ?? new Error("Chunk upload failed");
		},
		[recomputeProgress]
	);

	const upload = useCallback(
		async (file: File) => {
			// If the caller starts a new upload while one is still in
			// flight (e.g. user re-selects a file), abort the previous
			// one cleanly before resetting refs. Without this, `reset()`
			// nulls the abort controller without firing it, so chunk
			// requests for the prior upload keep posting and the server
			// keeps the prior upload's metadata + temp file until the
			// orphan reaper runs.
			if (abortRef.current || uploadIdRef.current) {
				try {
					await abort();
				} catch {
					// Best-effort; even on failure we still want to start fresh.
				}
			}

			reset();
			const controller = new AbortController();
			abortRef.current = controller;
			totalBytesRef.current = file.size;
			speedSamplesRef.current = [{ t: Date.now(), bytes: 0 }];
			setState(() => ({
				...initialState(),
				totalBytes: file.size,
				status: "initiating"
			}));

			try {
				const initResp = await assetClient.post<InitiateResponse>(
					"/api/v1.0/upload/video/chunked/initiate",
					{
						// Backend uses JsonNamingPolicy.SnakeCaseLower globally —
						// all request body keys must be snake_case to bind to
						// `InitiateChunkedUploadRequest`. All three props are
						// `required`, so a key mismatch produces a 400 with an
						// empty body before our handler ever runs.
						file_name: file.name,
						content_type: file.type,
						total_size_bytes: file.size
					},
					{ signal: controller.signal }
				);
				const { upload_id, chunk_size_bytes, total_chunks } = initResp.data;
				uploadIdRef.current = upload_id;

				setState((prev) => ({ ...prev, status: "uploading" }));

				// Build the queue of chunk indices and chew through it with a
				// fixed pool of workers. Limiting parallelism keeps us inside
				// the 60-permits-per-minute rate limit and avoids overwhelming
				// the user's uplink.
				const queue: number[] = [];
				for (let i = 0; i < total_chunks; i++) queue.push(i);

				const workers: Promise<void>[] = [];
				for (let w = 0; w < Math.min(PARALLELISM, total_chunks); w++) {
					workers.push(
						(async () => {
							while (queue.length > 0) {
								const index = queue.shift();
								if (index === undefined) return;
								const start = index * chunk_size_bytes;
								const end = Math.min(start + chunk_size_bytes, file.size);
								const chunk = file.slice(start, end);
								await uploadChunkWithRetry(upload_id, index, chunk, controller);
							}
						})()
					);
				}
				await Promise.all(workers);

				setState((prev) => ({ ...prev, status: "completing" }));
				const completeResp = await assetClient.post<CompleteResponse>(
					`/api/v1.0/upload/video/chunked/${upload_id}/complete`,
					null,
					{ signal: controller.signal }
				);
				setState((prev) => ({
					...prev,
					status: "done",
					assetId: completeResp.data.asset_id,
					progress: 1,
					bytesUploaded: file.size,
					etaSeconds: 0
				}));
				// Clear the in-flight refs so a subsequent `abort()` after
				// successful completion is a no-op (no spurious DELETE,
				// no status flip from "done" to "aborted").
				abortRef.current = null;
				uploadIdRef.current = null;
				return completeResp.data.asset_id;
			} catch (err) {
				// Distinguish chunk-failure (we abort the controller from
				// inside `uploadChunkWithRetry` to stop siblings) from a
				// user-initiated cancel (`abort()` set abortRef to null).
				const isFatalChunkFailure = fatalChunkFailureRef.current;
				const aborted =
					!isFatalChunkFailure &&
					(controller.signal.aborted ||
						(err instanceof Error && err.message === "aborted") ||
						(err as { name?: string })?.name === "CanceledError" ||
						(err as { name?: string })?.name === "AbortError");
				setState((prev) => {
					// Preserve a terminal state set by `abort()` racing with
					// the catch — don't flip "aborted" → "error".
					if (prev.status === "aborted" || prev.status === "done") {
						return prev;
					}
					return {
						...prev,
						status: aborted ? "aborted" : "error",
						error: aborted ? null : ((err as Error).message ?? "Upload failed")
					};
				});
				abortRef.current = null;
				uploadIdRef.current = null;
				return null;
			}
		},
		[abort, reset, uploadChunkWithRetry]
	);

	useEffect(() => {
		// Abort an in-flight upload if the host component unmounts.
		// We fire BOTH the AbortController (cancels in-flight chunks)
		// AND the DELETE endpoint (releases the server-side Redis state
		// + temp file immediately, instead of waiting up to 25 hours
		// for the orphan reaper). At 50 GB ceilings the reclaim matters.
		return () => {
			const controller = abortRef.current;
			const uploadId = uploadIdRef.current;
			abortRef.current = null;
			uploadIdRef.current = null;
			controller?.abort();
			if (uploadId) {
				try {
					const result = assetClient.delete(
						`/api/v1.0/upload/video/chunked/${uploadId}`
					);
					// Defensive .then chain — if the http client returned
					// something non-thenable, swallow rather than crash the
					// unmount commit.
					if (
						result &&
						typeof (result as Promise<unknown>).then === "function"
					) {
						(result as Promise<unknown>).catch(() => {
							// Best-effort; server reaper handles the rest.
						});
					}
				} catch {
					// Even synchronous throws shouldn't break unmount.
				}
			}
		};
	}, []);

	return {
		state,
		upload,
		abort,
		reset
	};
}
